import { useState } from "react";
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
import { validateField, validateEmailWithExistence } from "../utils/validationUtils";

const useForm = (initialValues = {}, requiredFields = {}, inputTypes = {}, checkEmailExistsFunc = null) => {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouchedState] = useState({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const handleInputChange = (name, value) => {
    let updatedValue = value;
    // Handle dropdown selections
    if (
      inputTypes[name]?.type === COMMON_FIELD_TYPES.DROPDOWN &&
      typeof value === "object"
    ) {
      updatedValue = value?.[inputTypes[name].displayKey] || "";
    }
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: updatedValue };
      // Validate using updated formData
      const error = validateField(
        name,
        inputTypes[name],
        updatedValue?.trim?.() ?? updatedValue,
        newFormData,
        requiredFields
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
      return newFormData;
    });
  };

  const handleBlur = async (name, value) => {
    let sanitizedValue = value;
    
    // Sanitizing the date input
    if (inputTypes[name] === COMMON_FIELD_TYPES.DATE_PICKER) {
      sanitizedValue = sanitizedValue ? new Date(sanitizedValue)?.toISOString()?.split("T")[0] : "";
    }
    
    // Sanitizing the time input
    if (inputTypes[name] === COMMON_FIELD_TYPES.TIME_PICKER && sanitizedValue) {
      const [hourMinute, period] = sanitizedValue.split(" ");
      if (hourMinute && period) {
        const [hour, minute] = hourMinute.split(":");
        const sanitizedHour = hour?.padStart(2, "0");
        const sanitizedMinute = minute?.padStart(2, "0");
        sanitizedValue = `${sanitizedHour}:${sanitizedMinute} ${period.toUpperCase()}`;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    setTouchedState((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Special handling for email fields with backend validation
    if (name === 'email' && inputTypes[name] === COMMON_FIELD_TYPES.EMAIL_FIELD && checkEmailExistsFunc) {
      setIsCheckingEmail(true);
      
      try {
        const error = await validateEmailWithExistence(sanitizedValue, checkEmailExistsFunc);
        setErrors((prev) => ({
          ...prev,
          [name]: error,
        }));
      } catch (err) {
        console.error("Email validation error:", err);
        // Fallback to basic validation if API fails
        const error = validateField(
          name,
          inputTypes[name],
          sanitizedValue,
          formData,
          requiredFields
        );
        setErrors((prev) => ({
          ...prev,
          [name]: error,
        }));
      } finally {
        setIsCheckingEmail(false);
      }
    } else {
      // Regular field validation
      const error = validateField(
        name,
        inputTypes[name],
        sanitizedValue,
        formData,
        requiredFields
      );
      
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Function to clear errors on focus
  const clearError = (name) => {
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = async () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      newErrors[key] = validateField(
        key,
        inputTypes[key],
        typeof formData[key] === "string" ? formData[key]?.trim() : formData[key],
        formData,
        requiredFields
      );
    });
    if (checkEmailExistsFunc && formData.email) {
      newErrors.email = await validateEmailWithExistence(formData.email, checkEmailExistsFunc);
    }
    
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  return {
    formData,
    errors,
    touched,
    isCheckingEmail,
    handleInputChange,
    handleBlur,
    clearError,
    validateForm,
    setTouched: setTouchedState,
    setErrors,
    setFormData,
  };
};

export default useForm;